import { Player, SkillDistribution } from './Model';

// Centralized color configuration - mirrors CSS variables in common.css
// These colors should be kept in sync with the theme colors defined in CSS
const themeColors = {
  success: {
    base: '#22c55e',
    light: 'rgba(34, 197, 94, 0.08)',
    border: 'rgba(34, 197, 94, 0.25)',
  },
  primary: {
    base: '#3b82f6',
    light: 'rgba(59, 130, 246, 0.08)',
    border: 'rgba(59, 130, 246, 0.25)',
  },
  warning: {
    base: '#fbbf24',
    light: 'rgba(251, 191, 36, 0.08)',
    border: 'rgba(251, 191, 36, 0.25)',
  },
  danger: {
    base: '#ef4444',
    light: 'rgba(239, 68, 68, 0.08)',
    border: 'rgba(239, 68, 68, 0.25)',
  },
  text: {
    primary: '#fafafa',
    muted: '#a8a8b3', // Matches --color-text-muted in common.css
  },
};

// Function to get color based on player's skill total with configurable boundaries
export function getSkillColor(player: Player, distribution?: SkillDistribution): { background: string; border: string; text: string; accent: string; category: string } {
  const skillCount = Object.keys(player.skills).length;
  const maxPossibleScore = skillCount * 5; // Each skill can be 1-5
  const minPossibleScore = skillCount * 1; // Minimum score
  const scoreRange = maxPossibleScore - minPossibleScore;

  // Use default distribution if none provided (15, 45, 85)
  const defaultDistribution: SkillDistribution = {
    developing: 15,
    average: 45,
    good: 85
  };

  const dist = distribution || defaultDistribution;

  // Calculate boundaries (working from top down)
  const excellentThreshold = maxPossibleScore - (scoreRange * (100 - dist.good) / 100);
  const goodThreshold = maxPossibleScore - (scoreRange * (100 - dist.average) / 100);
  const averageThreshold = maxPossibleScore - (scoreRange * (100 - dist.developing) / 100);

  const totalScore = player.totalScore;

  if (totalScore >= excellentThreshold) {
    // Top tier - Excellent (green)
    return { background: themeColors.success.light, border: themeColors.success.border, text: themeColors.text.primary, accent: themeColors.success.base, category: 'Excellent' };
  } else if (totalScore >= goodThreshold) {
    // Second tier - Good (blue)
    return { background: themeColors.primary.light, border: themeColors.primary.border, text: themeColors.text.primary, accent: themeColors.primary.base, category: 'Good' };
  } else if (totalScore >= averageThreshold) {
    // Third tier - Average (amber)
    return { background: themeColors.warning.light, border: themeColors.warning.border, text: themeColors.text.primary, accent: themeColors.warning.base, category: 'Average' };
  } else {
    // Bottom tier - Developing (red)
    return { background: themeColors.danger.light, border: themeColors.danger.border, text: themeColors.text.muted, accent: themeColors.danger.base, category: 'Developing' };
  }
}
